import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';
import type { AdminInvite, CreateInviteBody, InviteWithLink } from '@/types/invites';

export const ADMIN_INVITES_KEY = ['admin', 'invites'] as const;

export function useAdminInvites(): UseQueryResult<AdminInvite[]> {
  return useQuery({
    queryKey: ADMIN_INVITES_KEY,
    queryFn: async () => (await apiRequest<{ items: AdminInvite[] }>('/api/admin/invites')).items,
  });
}

function useInvalidateInvites(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ADMIN_INVITES_KEY });
}

/** Errors are shown inline by the form, so the global toast is suppressed (meta.silent). */
export function useCreateInvite(): UseMutationResult<InviteWithLink, Error, CreateInviteBody> {
  const invalidate = useInvalidateInvites();
  return useMutation({
    mutationFn: (body) => apiRequest<InviteWithLink>('/api/admin/invites', { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useRegenerateInviteLink(): UseMutationResult<InviteWithLink, Error, string> {
  const invalidate = useInvalidateInvites();
  return useMutation({
    mutationFn: (id) => apiRequest<InviteWithLink>(`/api/admin/invites/${id}/link`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useCancelInvite(): UseMutationResult<{ invite: AdminInvite }, Error, string> {
  const invalidate = useInvalidateInvites();
  return useMutation({
    mutationFn: (id) => apiRequest<{ invite: AdminInvite }>(`/api/admin/invites/${id}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
